// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 2.1
// updated  : 2026-06-20
// protected: NO  -> Parity P1-6: SOP Detail (14 SOP V2-akurat).
//            +search (kode/judul/isi), +filter role (tabs), +tombol "SOP untuk role saya",
//            +Accordion 14 SOP dengan bagian Proses Eksternal & Internal, +badge kode SOP.
//            v2.1 (Audit Fix Batch P0 / D-021): blok "Proses Internal (Sistem)" hanya
//            tampil untuk OWNER & KACAB; role operasional hanya melihat Proses Eksternal.
//            Akses halaman: SEMUA role (tanpa read-tracking).
// =============================================================================
// Halaman SOP & Panduan (semua role): ringkasan per-role + 14 SOP detail V2-akurat.
import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { BookOpen, Search, UserCheck, ArrowRightFromLine, Cog, FileText } from "lucide-react";
import { useV2Auth } from "@/v2/V2AuthContext";
import { sopForRole, SOP_DETAIL, roleTag, ROLE_TAG_LABEL } from "@/v2/sopContent";

// Urutan tab filter (kiri -> kanan)
const FILTERS = ["ALL", "OWNER", "KACAB", "GUDANG", "PRODUKSI", "STORE"];

export default function SOPPage() {
  const { me } = useV2Auth();
  const myTag = roleTag(me?.role);
  const roleSop = sopForRole(me?.role);
  // Audit Fix Batch P0 (D-021): blok "Proses Internal (Sistem)" hanya untuk OWNER & KACAB.
  // Role operasional (PRODUKSI/GUDANG/STORE) hanya melihat Proses Eksternal + langkah kerja.
  const canSeeInternal = myTag === "OWNER" || myTag === "KACAB";

  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SOP_DETAIL.filter((s) => {
      // Filter role: "ALL" -> semua; selain itu cocokkan tag atau SOP global ("ALL")
      const roleOk =
        filter === "ALL" || s.roles.includes(filter) || s.roles.includes("ALL");
      if (!roleOk) return false;
      if (!q) return true;
      const haystack = [
        s.code, s.title, s.summary,
        ...(s.external || []), ...(s.internal || []),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [filter, query]);

  return (
    <div className="max-w-4xl space-y-6" data-testid="v2-sop-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
          <BookOpen className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="sop-page-title">
            SOP &amp; Panduan
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Panduan operasional ETAMSINV — akurat sesuai sistem berjalan. Dapat diakses semua role.
          </p>
        </div>
      </div>

      {/* Ringkasan SOP role saya */}
      <Card className="p-5 rounded-2xl border-primary/20 bg-primary/[0.03]" data-testid="sop-role-summary">
        <div className="flex items-center gap-2 mb-3">
          <UserCheck className="w-4 h-4 text-primary" />
          <h2 className="text-base font-semibold">{roleSop.title}</h2>
          <Badge variant="secondary" className="ml-auto">{ROLE_TAG_LABEL[myTag] || "Role"}</Badge>
        </div>
        <ul className="space-y-2 text-sm leading-relaxed list-disc pl-5 text-muted-foreground">
          {roleSop.points.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </Card>

      {/* Toolbar: search + filter role */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari SOP berdasarkan kode, judul, atau isi…"
            className="pl-9 rounded-xl"
            data-testid="sop-search-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <Button
              key={f}
              type="button"
              size="sm"
              variant={filter === f ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setFilter(f)}
              data-testid={`sop-filter-${f.toLowerCase()}`}
            >
              {f === "ALL" ? "Semua" : ROLE_TAG_LABEL[f]}
            </Button>
          ))}
          {myTag !== "ALL" && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="rounded-full ml-auto"
              onClick={() => setFilter(myTag)}
              data-testid="sop-filter-my-role"
            >
              <UserCheck className="w-3.5 h-3.5 mr-1.5" />
              SOP untuk role saya
            </Button>
          )}
        </div>
      </div>

      {/* Daftar 14 SOP */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-muted/30">
          <span className="text-sm font-medium">Daftar SOP</span>
          <Badge variant="outline" data-testid="sop-result-count">
            {filtered.length} SOP
          </Badge>
        </div>

        {filtered.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="sop-empty-state">
            <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
            Tidak ada SOP yang cocok dengan pencarian / filter.
          </div>
        ) : (
          <Accordion type="single" collapsible className="px-2">
            {filtered.map((s) => (
              <AccordionItem key={s.code} value={s.code} data-testid={`sop-item-${s.code}`}>
                <AccordionTrigger className="px-3 hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <Badge className="shrink-0 font-mono" data-testid={`sop-code-${s.code}`}>{s.code}</Badge>
                    <div>
                      <div className="font-medium">{s.title}</div>
                      <div className="text-xs text-muted-foreground font-normal mt-0.5 line-clamp-2">
                        {s.summary}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {s.roles.map((r) => (
                      <Badge key={r} variant="secondary" className="text-[11px]">
                        {ROLE_TAG_LABEL[r] || r}
                      </Badge>
                    ))}
                  </div>

                  <div className={canSeeInternal ? "grid gap-5 md:grid-cols-2" : "grid gap-5"}>
                    {/* Proses Eksternal */}
                    <section data-testid={`sop-external-${s.code}`}>
                      <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
                        <ArrowRightFromLine className="w-4 h-4 text-primary" />
                        Proses Eksternal
                      </div>
                      <ol className="space-y-1.5 list-decimal pl-5 text-sm leading-relaxed text-muted-foreground">
                        {(s.external || []).map((p, i) => <li key={i}>{p}</li>)}
                      </ol>
                    </section>

                    {/* Proses Internal — hanya OWNER & KACAB */}
                    {canSeeInternal && (
                    <section data-testid={`sop-internal-${s.code}`}>
                      <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
                        <Cog className="w-4 h-4 text-primary" />
                        Proses Internal (Sistem)
                      </div>
                      <ul className="space-y-1.5 list-disc pl-5 text-sm leading-relaxed text-muted-foreground">
                        {(s.internal || []).map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </section>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Card>

      <Separator />
      <p className="text-xs text-muted-foreground">
        Catatan: konten SOP mengacu pada sistem ETAMS V2 yang berjalan. Fitur yang belum memiliki UI
        penuh (mis. timeline QR) ditandai sebagai roadmap dan tidak di-overclaim.
      </p>
    </div>
  );
}
